import { useMemo, useState } from 'react'
import type { ProductRow, Supplier } from '../../../types'
import { BADGE_BASE } from '../../layout/mobile/mobileTokens'

interface SelectableRow extends ProductRow {
  qty: number
  selected: boolean
}

interface SupplierGroup {
  supplier: Supplier
  rows: SelectableRow[]
}

interface Props {
  groups: SupplierGroup[]
  emitting: string | null
  onToggle: (id: string, current: boolean) => void
  onToggleAll: (rows: SelectableRow[], select: boolean) => void
  onSetQty: (id: string, qty: number) => void
  onEmit: (supplier: Supplier, rows: SelectableRow[]) => void
}

const fmtMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })

export default function MobilePurchaseReport(props: Props) {
  const [selected, setSelected] = useState<string | null>(null)

  const selectedGroup = useMemo(
    () => selected ? props.groups.find(g => g.supplier.id === selected) ?? null : null,
    [props.groups, selected]
  )

  if (selectedGroup) {
    return (
      <SupplierDetail
        {...props}
        group={selectedGroup}
        onBack={() => setSelected(null)}
      />
    )
  }

  return <SupplierList groups={props.groups} onOpen={setSelected} />
}

// ─── Lista de fornecedores ────────────────────────────────────────────────

interface ListProps {
  groups: SupplierGroup[]
  onOpen: (supplierId: string) => void
}

function SupplierList({ groups, onOpen }: ListProps) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-ink-faint px-1">
        Toque em um fornecedor para ver os itens e emitir o pedido.
      </p>
      {groups.map(({ supplier, rows }) => {
        const selectedRows = rows.filter(r => r.selected && r.qty > 0)
        const total = selectedRows.reduce((sum, r) => sum + r.qty * r.cost, 0)
        const hasConsig = rows.some(r => r.kanban_status === 'Consignado')
        return (
          <button
            key={supplier.id}
            onClick={() => onOpen(supplier.id)}
            className="w-full text-left bg-surface active:bg-sunken border border-line rounded-xl p-3 touch-action-manip no-tap-highlight"
          >
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-ink font-bold text-sm leading-tight line-clamp-2 min-w-0 flex-1">
                {supplier.name}
              </h3>
              <span className="shrink-0 text-ink-faint text-lg leading-none">›</span>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-sunken text-ink-muted font-medium">
                {selectedRows.length}/{rows.length} selecionados
              </span>
              {hasConsig && (
                <span className="px-2 py-0.5 rounded-full bg-[#FFC000]/20 border border-[#FFC000]/40 text-[#FFC000] font-medium">
                  Consignado
                </span>
              )}
              {supplier.lead_time_days != null && (
                <span className="text-ink-faint">LT {supplier.lead_time_days}d</span>
              )}
              <span className="ml-auto text-accent font-bold tabular-nums">{fmtMoney(total)}</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ─── Detalhe do fornecedor ────────────────────────────────────────────────

interface DetailProps extends Props {
  group: SupplierGroup
  onBack: () => void
}

function SupplierDetail({
  group, emitting, onToggle, onToggleAll, onSetQty, onEmit, onBack,
}: DetailProps) {
  const { supplier, rows: gRows } = group
  const selectedRows = gRows.filter(r => r.selected && r.qty > 0)
  const total = selectedRows.reduce((sum, r) => sum + r.qty * r.cost, 0)
  const allSelected = gRows.every(r => r.selected)

  return (
    <div className="space-y-3">
      {/* Header sticky */}
      <div className="sticky top-0 -mx-3 px-3 py-2 bg-paper/95 backdrop-blur-sm z-10 border-b border-line">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-sunken active:bg-line text-ink text-sm font-medium touch-action-manip"
          >
            <span aria-hidden>←</span>
            <span>Voltar</span>
          </button>
          <button
            onClick={() => onToggleAll(gRows, !allSelected)}
            className="ml-auto text-[11px] text-brand-300 active:text-brand-200 px-2.5 py-1.5 rounded-lg bg-sunken active:bg-line touch-action-manip font-medium"
          >
            {allSelected ? '☐ Nenhum' : '☑ Todos'}
          </button>
        </div>
        <div className="mt-2 min-w-0">
          <h3 className="text-ink font-bold text-base leading-tight line-clamp-2">{supplier.name}</h3>
          <div className="flex items-center gap-2 mt-0.5 text-[11px]">
            <span className="text-ink-faint">
              {selectedRows.length}/{gRows.length} selecionados
              {supplier.lead_time_days != null ? ` · LT ${supplier.lead_time_days}d` : ''}
            </span>
            <span className="ml-auto text-accent font-bold tabular-nums">{fmtMoney(total)}</span>
          </div>
        </div>
      </div>

      {/* Cards de item */}
      <div className="space-y-2">
        {gRows.map(r => (
          <label
            key={r.id}
            className={`flex items-start gap-2 bg-surface border border-line rounded-xl p-3 active:bg-sunken touch-action-manip ${r.selected ? '' : 'opacity-50'}`}
          >
            <input
              type="checkbox"
              checked={r.selected}
              onChange={() => onToggle(r.id, r.selected)}
              className="mt-1 w-5 h-5 accent-brand-600 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`${BADGE_BASE} ${r.kanban_status === 'Consignado' ? 'bg-[#FFC000] text-gray-900' : 'bg-[#ED7D31]/40 text-[#FFD8B0]'}`}>
                  {r.kanban_status === 'Consignado' ? 'CONSIG' : 'COMPRA'}
                </span>
                <span className="font-mono text-[10px] text-ink-muted truncate">{r.sku}</span>
              </div>
              <p className="text-ink text-sm font-medium leading-tight line-clamp-2 mb-2">{r.name}</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={e => { e.preventDefault(); onSetQty(r.id, Math.max(0, r.qty - 1)) }}
                    className="w-9 h-9 rounded-lg bg-sunken active:bg-line text-ink text-lg font-bold flex items-center justify-center touch-action-manip"
                  >−</button>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={r.qty}
                    onChange={e => onSetQty(r.id, Number(e.target.value))}
                    onClick={e => e.preventDefault()}
                    className="w-16 h-9 bg-paper border border-line rounded-lg text-center text-ink text-sm font-bold tabular-nums"
                  />
                  <button
                    onClick={e => { e.preventDefault(); onSetQty(r.id, r.qty + 1) }}
                    className="w-9 h-9 rounded-lg bg-sunken active:bg-line text-ink text-lg font-bold flex items-center justify-center touch-action-manip"
                  >+</button>
                </div>
                <div className="flex-1 text-right">
                  <p className="text-[10px] text-ink-faint leading-none">subtotal</p>
                  <p className="text-sm text-ink font-bold tabular-nums leading-tight">{fmtMoney(r.qty * r.cost)}</p>
                </div>
              </div>
            </div>
          </label>
        ))}
      </div>

      {/* Botão emitir fixo no fim com padding pra tab bar */}
      <div className="pb-6">
        <button
          onClick={() => onEmit(supplier, gRows)}
          disabled={emitting === supplier.id || selectedRows.length === 0}
          className="w-full py-4 rounded-xl bg-brand-600 active:bg-brand-700 disabled:opacity-50 text-white text-sm font-bold touch-action-manip"
        >
          {emitting === supplier.id
            ? 'Emitindo…'
            : `📨 Emitir Pedido (${selectedRows.length} itens · ${fmtMoney(total)})`}
        </button>
      </div>
    </div>
  )
}
